import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import {
  CheckCircle2,
  Circle,
  ChevronDown,
  ChevronUp,
  X,
  Sparkles,
  Shield,
  AlertTriangle
} from 'lucide-react';
import { getPriorityColor } from '../../lib/onboardingSteps';

/**
 * OnboardingChecklist component
 * Persistent checklist widget showing completion progress
 */
const OnboardingChecklist = ({
  items,
  completedItems,
  progress,
  onDismiss,
  onRestartTour,
  allCompleted,
  defaultMinimized = true
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isExpanded, setIsExpanded] = useState(true);

  // Persist minimized state to localStorage so it survives re-mounts and page navigation
  const storageKey = 'bohol_checklist_minimized';
  const getInitialMinimized = () => {
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored !== null) return stored === 'true';
    } catch (e) { /* ignore */ }
    return defaultMinimized;
  };
  const [isMinimized, setIsMinimizedRaw] = useState(getInitialMinimized);
  const setIsMinimized = useCallback((value) => {
    setIsMinimizedRaw(value);
    try { localStorage.setItem(storageKey, String(value)); } catch (e) { /* ignore */ }
  }, []);

  // Don't show if all completed
  if (allCompleted) {
    return null;
  }

  // Minimized view
  if (isMinimized) {
    return (
      <div className="fixed bottom-4 right-4 z-50 md:bottom-6 md:right-6">
        <Button
          onClick={() => setIsMinimized(false)}
          className="rounded-full w-14 h-14 shadow-lg bg-primary hover:bg-primary/90 flex items-center justify-center"
        >
          <div className="relative">
            <Sparkles className="w-6 h-6" />
            {progress.total - progress.completed > 0 && (
              <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                {progress.total - progress.completed}
              </span>
            )}
          </div>
        </Button>
      </div>
    );
  }

  const isItemComplete = (itemId) => completedItems.includes(itemId);

  const handleAction = (item) => {
    if (item.actionPath) {
      navigate(item.actionPath);
    }
  };

  return (
    <div className="fixed bottom-4 right-4 z-50 w-[calc(100%-2rem)] max-w-sm md:bottom-6 md:right-6 md:w-96">
      <Card className="shadow-xl border-2 border-primary/20 bg-white/95 backdrop-blur">
        {/* Header */}
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-primary" />
              <CardTitle className="text-base font-semibold">
                {t('onboarding.checklist.title')}
              </CardTitle>
            </div>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={() => setIsMinimized(true)}
              >
                <ChevronDown className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={onDismiss}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Progress bar */}
          <div className="mt-3">
            <div className="flex items-center justify-between text-sm mb-1">
              <span className="text-muted-foreground">
                {t('onboarding.checklist.progress')}
              </span>
              <span className="font-medium text-primary">
                {progress.completed}/{progress.total}
              </span>
            </div>
            <Progress value={progress.percentage} className="h-2" />
          </div>
        </CardHeader>

        <CardContent className="pt-2 pb-4">
          {/* Toggle expand */}
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-between mb-2 text-muted-foreground hover:text-foreground"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            <span className="text-sm">
              {isExpanded
                ? t('onboarding.checklist.hideItems')
                : t('onboarding.checklist.showItems')}
            </span>
            {isExpanded ? (
              <ChevronUp className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )}
          </Button>

          {/* Checklist items */}
          {isExpanded && (
            <div className="space-y-2 max-h-64 overflow-y-auto" style={{ overscrollBehavior: 'contain' }}>
              {items.map((item) => {
                const completed = isItemComplete(item.id);
                const isRequired = item.required;
                const isCritical = item.priority === 'critical';

                return (
                  <div
                    key={item.id}
                    role={!completed && item.actionPath ? 'button' : undefined}
                    tabIndex={!completed && item.actionPath ? 0 : undefined}
                    onClick={() => {
                      if (!completed && item.actionPath) handleAction(item);
                    }}
                    onKeyDown={(e) => {
                      if (!completed && item.actionPath && (e.key === 'Enter' || e.key === ' ')) {
                        e.preventDefault();
                        handleAction(item);
                      }
                    }}
                    className={`flex items-start gap-3 p-3 rounded-lg border transition-colors ${
                      completed
                        ? 'bg-green-50 border-green-200'
                        : isCritical
                        ? 'bg-red-50 border-red-200 cursor-pointer active:bg-red-100'
                        : 'bg-gray-50 border-gray-200 hover:bg-gray-100 cursor-pointer active:bg-gray-200'
                    }`}
                  >
                    {/* Checkbox */}
                    <div className="flex-shrink-0 mt-0.5">
                      {completed ? (
                        <CheckCircle2 className="w-5 h-5 text-green-600" />
                      ) : isCritical ? (
                        <AlertTriangle className="w-5 h-5 text-red-500" />
                      ) : (
                        <Circle className="w-5 h-5 text-gray-400" />
                      )}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span
                          className={`text-sm font-medium ${
                            completed
                              ? 'text-green-700 line-through'
                              : 'text-gray-900'
                          }`}
                        >
                          {t(item.labelKey)}
                        </span>
                        {isRequired && !completed && (
                          <Badge
                            variant="outline"
                            className="text-xs bg-red-100 text-red-700 border-red-200"
                          >
                            {t('onboarding.checklist.required')}
                          </Badge>
                        )}
                        {item.id === 'verify_id' && !completed && (
                          <Shield className="w-4 h-4 text-blue-500" />
                        )}
                      </div>
                      <p
                        className={`text-xs mt-0.5 ${
                          completed ? 'text-green-600' : 'text-muted-foreground'
                        }`}
                      >
                        {t(item.descriptionKey)}
                      </p>

                      {/* Action hint for incomplete items */}
                      {!completed && item.actionPath && (
                        <span className="text-xs text-primary mt-1 inline-block">
                          {t(item.actionLabelKey)} &rarr;
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Footer actions */}
          <div className="mt-4 pt-3 border-t flex items-center justify-between">
            <Button
              variant="ghost"
              size="sm"
              className="text-xs text-muted-foreground"
              onClick={onRestartTour}
            >
              <Sparkles className="w-3 h-3 mr-1" />
              {t('onboarding.checklist.takeTour', 'Take a Tour')}
            </Button>

            <Button
              variant="ghost"
              size="sm"
              className="text-xs text-muted-foreground"
              onClick={onDismiss}
            >
              {t('onboarding.checklist.dismiss')}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default OnboardingChecklist;
